import React, { useState } from 'react';
import { X, Send, Mail, UserPlus, Briefcase, Copy } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const VARIANTS = {
  member: {
    title: 'Invite a member',
    icon: UserPlus,
    description: 'They\u2019ll receive an email with a one-tap signup link. New members invited by a Tax Specialist are auto-assigned to that specialist.',
    cta: 'Send member invite',
  },
  specialist: {
    title: 'Invite a Tax Specialist',
    icon: Briefcase,
    description: 'They\u2019ll receive an email with a link to the Tax Specialist application form. Admin approval is required before an account is created.',
    cta: 'Send specialist invite',
  },
};

/**
 * Reusable invitation modal. Used by admin + tax specialist surfaces.
 *
 * Props
 * -----
 * - ``kind``: 'member' | 'specialist'
 * - ``open`` / ``onClose``
 * - ``onSent`` (optional): callback receiving the created invitation row
 */
export default function InviteModal({ kind = 'member', open, onClose, onSent }) {
  const [form, setForm] = useState({ email: '', name: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null);

  if (!open) return null;
  const variant = VARIANTS[kind];
  const Icon = variant.icon;

  const reset = () => { setForm({ email: '', name: '', message: '' }); setSent(null); };
  const close = () => { reset(); onClose && onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Please enter an email'); return; }
    setSubmitting(true);
    try {
      const row = await api.createInvitation({
        kind, email: form.email, name: form.name, message: form.message,
      });
      setSent(row);
      toast.success('Invitation sent');
      onSent && onSent(row);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send invitation');
    } finally { setSubmitting(false); }
  };

  const inviteUrl = sent
    ? (kind === 'member'
        ? `${window.location.origin}/signup?invite=${sent.token}`
        : `${window.location.origin}/apply/specialist?invite=${sent.token}`)
    : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Link copied');
    } catch { toast.error('Could not copy'); }
  };

  return (
    <div data-testid={`invite-modal-${kind}`}
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#fff8e3] text-[#c89200] flex items-center justify-center">
              <Icon className="w-4 h-4" />
            </div>
            <h3 className="font-bold text-[#14110f]">{variant.title}</h3>
          </div>
          <button onClick={close} className="text-neutral-400 hover:text-[#14110f]" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {sent ? (
          <div className="px-5 py-5 space-y-4">
            <div className="rounded-md bg-emerald-50 border border-emerald-100 px-4 py-3">
              <p className="text-[13.5px] font-semibold text-emerald-800">
                Invitation sent to <strong>{sent.email}</strong>
              </p>
              <p className="text-[12.5px] text-emerald-700 mt-1">
                They\u2019ll get an email shortly. The link expires on {new Date(sent.expires_at).toLocaleDateString()}.
              </p>
            </div>
            <div>
              <p className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Shareable link</p>
              <div className="mt-1.5 flex items-stretch gap-2">
                <input data-testid="invite-link-input" readOnly value={inviteUrl}
                       className="flex-1 px-3 py-2 rounded-md border border-neutral-200 text-[12.5px] font-mono text-[#14110f] bg-neutral-50" />
                <button data-testid="invite-link-copy" onClick={copyLink}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#14110f] text-white text-[12px] font-semibold">
                  <Copy className="w-3.5 h-3.5" />Copy
                </button>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-4">
              <button onClick={reset}
                      className="text-[13px] text-neutral-500 hover:text-[#14110f]">Send another</button>
              <button onClick={close}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold text-[13px]">
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-4 space-y-3">
            <p className="text-[12.5px] text-neutral-500 leading-19px">{variant.description}</p>
            <label className="block">
              <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Email</span>
              <div className="mt-1 relative">
                <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input data-testid={`invite-${kind}-email`} type="email" required
                       value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       placeholder="name@example.com"
                       className="w-full pl-9 pr-3 py-2.5 rounded-md border border-neutral-200 text-[14px] text-[#14110f] placeholder:text-neutral-400 focus:border-[#f5b300] outline-none bg-white" />
              </div>
            </label>
            <label className="block">
              <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Name (optional)</span>
              <input data-testid={`invite-${kind}-name`}
                     value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Their full name"
                     className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[14px] text-[#14110f] placeholder:text-neutral-400 focus:border-[#f5b300] outline-none bg-white" />
            </label>
            <label className="block">
              <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Personal note (optional)</span>
              <textarea data-testid={`invite-${kind}-message`}
                        value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        rows={3}
                        placeholder={kind === 'member' ? 'A warm hello and what to expect from the portal\u2026' : 'What you\u2019d love them to know about WyseTribe\u2026'}
                        className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] text-[#14110f] placeholder:text-neutral-400 focus:border-[#f5b300] outline-none bg-white" />
            </label>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-100 pt-3">
              <button type="button" onClick={close} className="text-[13px] text-neutral-500 hover:text-[#14110f]">Cancel</button>
              <button type="submit" data-testid={`invite-${kind}-submit`} disabled={submitting}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold text-[13px]">
                <Send className="w-3.5 h-3.5" />{submitting ? 'Sending\u2026' : variant.cta}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
